import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import { useAuth } from '../contexts/AuthContext';
import { COLORS } from '../lib/styleGuide';
import { 
  Document,
  TimeCircle, 
  Danger, 
  ChevronDown,
  ChevronUp,
  CloseSquare
} from 'react-iconly';



export function ChecklistWidget({ setupFeeCompleted = false }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  const {
    loading,
    error,
    getStepStatus,
    getOverallProgress,
    isComplete,
    checklistData
  } = useOnboardingStatus();

  // Generate store settings URL dynamically
  const getStoreSettingsUrl = () => {
    if (currentUser?.uid) {
      return `/seller-hub/store-settings/${currentUser.uid}`;
    }
    return '/seller-hub/store-settings/unknown';
  };

  const CHECKLIST_STEPS = [
    {
      id: 'setup_fee',
      title: 'Pay Setup Fee',
      actionText: 'Pay Fee',
      actionUrl: '/onboarding',
      required: true
    },
    {
      id: 'identity',
      title: 'Verify Your Identity',
      actionText: 'Verify Now',
      actionUrl: '/onboarding/verify-identity',
      required: true
    },
    {
      id: 'payment',
      title: 'Connect Bank Account',
      actionText: 'Connect Bank',
      actionUrl: '/seller-hub/payments/bank',
      required: true
    },
    {
      id: 'documents',
      title: 'Upload Required Documents',
      actionText: 'Upload Documents',
      actionUrl: '/documents',
      required: true
    },
    {
      id: 'product',
      title: 'Create Your First Product',
      actionText: 'Add Product',
      actionUrl: '/add-product',
      required: true
    },
    {
      id: 'store',
      title: 'Publish Your Store',
      actionText: 'Publish Store',
      actionUrl: getStoreSettingsUrl(),
      required: true
    }
  ];

  // Check if mobile/tablet
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load widget state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('checklistWidgetExpanded');
    if (savedState !== null) {
      setIsExpanded(JSON.parse(savedState));
    }
  }, []);

  // Auto-expand if there are pending steps
  useEffect(() => {
    if (!isComplete() && getOverallProgress() < 100) {
      // Auto-expand if there are incomplete steps
      setIsExpanded(true);
    }
  }, [isComplete, getOverallProgress]);

  // Save widget state to localStorage
  useEffect(() => {
    localStorage.setItem('checklistWidgetExpanded', JSON.stringify(isExpanded));
  }, [isExpanded]);

  // Hide widget if all steps are completed
  useEffect(() => {
    if (isComplete()) {
      setIsVisible(false);
    }
  }, [isComplete]);

  // Debug logging for the entire widget
  useEffect(() => {
    console.log('ChecklistWidget: Received checklistData:', checklistData);
    console.log('ChecklistWidget: Received setupFeeCompleted prop:', setupFeeCompleted);
  }, [checklistData, setupFeeCompleted]);

  // Don't render if all steps are completed
  if (isComplete()) {
    return null;
  }

  const handleStepAction = (step) => {
    console.log('ChecklistWidget: handleStepAction called for step:', step.id, 'actionUrl:', step.actionUrl);
    
    if (step.id === 'identity') {
      // Handle identity verification step intelligently
      const identityStatus = getStepStatus('identity');
      
      if (identityStatus === 'completed') {
        // If already completed, show a message or do nothing
        return;
      } else if (identityStatus === 'inProgress') {
        // If in progress, check current verification status
        navigate('/onboarding/verify-identity');
      } else {
        // If not started, start the verification process
        navigate('/onboarding/verify-identity');
      }
    } else {
      // For other steps, navigate normally
      console.log('ChecklistWidget: Navigating to:', step.actionUrl);
      navigate(step.actionUrl);
    }
  };



  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Calculate progress including the setup fee completion prop
  const calculateProgress = () => {
    if (setupFeeCompleted) {
      // If setup fee is completed via prop, calculate progress including it
      const totalSteps = CHECKLIST_STEPS.length;
      const completedSteps = CHECKLIST_STEPS.filter(step => {
        if (step.id === 'setup_fee') return true; // Always count setup fee as completed
        return getStepStatus(step.id) === 'completed';
      }).length;
      return Math.round((completedSteps / totalSteps) * 100);
    }
    return getOverallProgress();
  };


  // Mobile floating action button
  if (isMobile) {
    return (
      <>
        {/* Floating Action Button */}
        <button
          onClick={toggleExpanded}
          className="fixed bottom-24 right-6 z-50 w-14 h-14 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
          style={{ background: COLORS.PRIMARY_GRADIENT }}
          aria-label="Open setup guide"
        >
          <Document set="bold" size={24} primaryColor="#FFFFFF" />
        </button>

        {/* Mobile Modal */}
        {isExpanded && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-end" onClick={toggleExpanded}>
            <div className="bg-white w-full h-[85vh] rounded-t-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                          Setup Guide
                        </h2>
                        <p className="text-sm text-gray-600">
                          Complete these steps to start selling
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={toggleExpanded}
                      className="text-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center w-8 h-8"
                      aria-label="Close setup guide"
                    >
                      <CloseSquare set="bold" size={24} primaryColor="#9CA3AF" />
                    </button>
                  </div>

                {/* Progress Bar */}
                <div className="mt-4 flex-shrink-0">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span>Progress</span>
                    <span>{calculateProgress()}% Complete</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${calculateProgress()}%`,
                        background: COLORS.PRIMARY_GRADIENT
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-4 overflow-y-auto flex-1 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch', maxHeight: 'calc(85vh - 120px)' }}>
                {loading ? (
                  <div className="animate-pulse space-y-3">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="h-16 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                ) : error ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <Danger set="bold" size={20} primaryColor="#EF4444" />
                      <span className="ml-3 text-red-800">
                        Error loading checklist: {error}
                      </span>
                    </div>
                  </div>
                ) : (
                                  <div className="space-y-3">
                  {CHECKLIST_STEPS.map((step) => {
                    let stepStatus = getStepStatus(step.id);
                    let isCompleted = stepStatus === 'completed';
                    
                    // Override setup fee status if prop is true
                    if (step.id === 'setup_fee' && setupFeeCompleted) {
                      stepStatus = 'completed';
                      isCompleted = true;
                    }
                    
                    // Debug logging for identity step
                    if (step.id === 'identity') {
                      console.log('ChecklistWidget: Identity step status:', {
                        stepStatus,
                        isCompleted,
                        setupFeeCompleted
                      });
                    }

                    return (
                      <div
                        key={step.id}
                        className="flex items-start p-3 rounded-lg transition-all duration-200 hover:bg-gray-50"
                      >
                        {/* Status Checkbox/Circle */}
                        <div className="w-6 h-6 mr-3 flex-shrink-0 mt-0.5">
                          {isCompleted ? (
                            <div className="w-4 h-4 rounded-full flex items-center justify-center"
                                 style={{ background: COLORS.PRIMARY_GRADIENT }}>
                            </div>
                          ) : (
                            <div className="w-4 h-4 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center">
                            </div>
                          )}
                        </div>

                        {/* Step Content - Clickable Title */}
                        <div className="flex-1 min-w-0 flex items-center">
                          <button
                            onClick={() => handleStepAction(step)}
                            className={`text-left w-full group ${
                              isCompleted ? 'cursor-default' : 'cursor-pointer hover:text-[#f4a261]'
                            }`}
                            disabled={isCompleted}
                          >
                            <h3 className={`font-medium text-gray-900 text-sm transition-colors ${
                              isCompleted 
                                ? 'line-through text-gray-500' 
                                : 'group-hover:text-[#f4a261]'
                            }`}>
                              {step.title}
                            </h3>
                          </button>
                        </div>


                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop floating widget
  return (
    <div className={`fixed bottom-24 right-6 z-50 transition-all duration-300 ${
      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
    }`}>
      {/* Collapsed State */}
      {!isExpanded && (
        <button
          onClick={toggleExpanded}
          className="bg-white rounded-lg shadow-lg border border-[#E0CFC2] p-4 hover:shadow-xl transition-all duration-200 cursor-pointer"
          aria-label="Expand setup guide"
        >
          <div className="flex items-center">
            <div className="text-left">
              <h3 className="text-sm font-semibold text-gray-900">
                Setup Guide
              </h3>
              <div className="flex items-center justify-between text-xs text-gray-600 mt-1">
                <span>Progress</span>
                <span>{calculateProgress()}%</span>
              </div>
              <div className="w-20 bg-gray-200 rounded-full h-1.5 mt-1">
                <div
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${calculateProgress()}%`,
                    background: COLORS.PRIMARY_GRADIENT
                  }}
                ></div>
              </div>
            </div>
            <ChevronDown set="regular" size={16} primaryColor="#9CA3AF" className="ml-2" />
          </div>
        </button>
      )}

      {/* Expanded State */}
      {isExpanded && (
                  <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-80 max-h-[calc(100vh-200px)] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        Setup Guide
                      </h2>
                      <p className="text-sm text-gray-600">
                        Complete these steps to start selling
                      </p>
                    </div>
                  </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleExpanded}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Collapse setup guide"
                >
                  <ChevronUp set="regular" size={16} primaryColor="#9CA3AF" />
                </button>

              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4 flex-shrink-0">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>Progress</span>
                <span>{calculateProgress()}% Complete</span>
              </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${calculateProgress()}%`,
                        background: COLORS.PRIMARY_GRADIENT
                      }}
                    ></div>
                  </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto" style={{ maxHeight: '60vh', WebkitOverflowScrolling: 'touch' }}>
            {loading ? (
              <div className="animate-pulse space-y-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-16 bg-gray-200 rounded"></div>
                ))}
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <Danger set="bold" size={20} primaryColor="#EF4444" />
                  <span className="ml-3 text-red-800 text-sm">
                    Error loading checklist: {error}
                  </span>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {CHECKLIST_STEPS.map((step) => {
                  let stepStatus = getStepStatus(step.id);
                  let isCompleted = stepStatus === 'completed';
                  
                  // Override setup fee status if prop is true
                  if (step.id === 'setup_fee' && setupFeeCompleted) {
                    stepStatus = 'completed';
                    isCompleted = true;
                  }

                  return (
                    <div
                      key={step.id}
                      className="flex items-start p-3 rounded-lg transition-all duration-200 hover:bg-gray-50"
                    >
                      {/* Status Checkbox/Circle */}
                      <div className="w-6 h-6 mr-3 flex-shrink-0 mt-0.5">
                        {isCompleted ? (
                          <div className="w-4 h-4 rounded-full flex items-center justify-center"
                               style={{ background: COLORS.PRIMARY_GRADIENT }}>
                          </div>
                        ) : (
                          <div className="w-4 h-4 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center">
                          </div>
                        )}
                      </div>

                                            {/* Step Content - Clickable Title */}
                      <div className="flex-1 min-w-0 flex items-center">
                        <button
                          onClick={() => handleStepAction(step)}
                          className={`text-left w-full group ${
                            isCompleted ? 'cursor-default' : 'cursor-pointer hover:text-[#f4a261]'
                          }`}
                          disabled={isCompleted}
                        >
                          <h3 className={`font-medium text-gray-900 text-sm transition-colors ${
                            isCompleted 
                              ? 'line-through text-gray-500' 
                              : 'group-hover:text-[#f4a261]'
                          }`}>
                            {step.title}
                          </h3>
                        </button>
                      </div>

                                            </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
